'use client';

import type { ReactNode } from 'react';
import { motion } from 'motion/react';

import { useInlineSign } from '@/i18n/direction';
import { spring } from '@/lib/motion';

/**
 * PANELİN TEK AÇ/KAPA ANAHTARI.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 İki durumlu bir denetim gerekiyorsa anahtar BUDUR
 * ═══════════════════════════════════════════════════════════════════════════
 * Deri (`.dash-switch`) zaten ortaktı ama üç ekran onu üç ayrı yerde kendi
 * `<button role="switch">`i ile sarıyordu: kural listesi (aç/kapa), kural
 * editörü (kanal seçimi), bildirim tercihleri. Üçü de aynı `aria-checked`,
 * aynı sap animasyonu, aynı 18px kaydırmayı tekrar yazmıştı — biri
 * düzeltilince diğerleri eski kalırdı.
 *
 * ---------------------------------------------------------------------------
 * Neden `<input type="checkbox">` değil
 * ---------------------------------------------------------------------------
 * Onay kutusu "bu maddeyi seçtim" demek ve gönderilecek bir formun parçası;
 * anahtar ise BİR DURUMU şimdi değiştiriyor. `role="switch"` ekran okuyucuya
 * "açık / kapalı" diye okunuyor, "işaretli / işaretsiz" diye değil.
 *
 * ---------------------------------------------------------------------------
 * Hareket
 * ---------------------------------------------------------------------------
 * Sap `x` ile kayıyor — düzen hesabı doğurmayan tek özellik (DESIGN.md §1).
 * Yay `snap` (400/22/0,6): küçük ve anlık şeyler. Anahtarın ağır hissetmesi
 * "önemli bir şey oldu" derdi; asıl ağırlık onay diyaloğunda.
 *
 * Azaltılmış harekette kök `<MotionConfig reducedMotion="user">` bunu
 * kendiliğinden kapatıyor; sap yine doğru uçta duruyor.
 */

/** Sapın kat ettiği yol: 42px iz − 2×3px dolgu − 18px sap. */
const KNOB_TRAVEL = 18;

/*
 * Sağdan sola dilde anahtar AYNALANIYOR: sap blok öğe ve `dir='rtl'`de izin
 * SAĞ ucunda duruyor (kapalı), açılınca sola gidiyor. Yol işaretle çarpılıyor;
 * soldan sağa davranış birebir aynı.
 */

export function DashSwitch({
  on,
  label,
  labelledBy,
  disabled,
  pending,
  onToggle,
}: {
  on: boolean;
  /** Erişilebilir ad. Yazı ayrı bir öğedeyse `labelledBy` kullanılır. */
  label?: string;
  labelledBy?: string;
  disabled?: boolean;
  /** Bir istek uçuyor: düğme kapalı ve satır sönük. */
  pending?: boolean;
  onToggle: () => void;
}) {
  const travel = KNOB_TRAVEL * useInlineSign();

  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      aria-labelledby={labelledBy}
      className="dash-switch"
      data-on={on}
      data-pending={pending || undefined}
      disabled={disabled || pending}
      onClick={onToggle}
    >
      <motion.span
        className="dash-switch-knob"
        aria-hidden="true"
        animate={{ x: on ? travel : 0 }}
        transition={spring.snap}
      />
    </button>
  );
}

/**
 * Anahtar + yazı, tek düğme.
 *
 * Hedef bütün satır (42px, kaba işaretçide 44) — yalnız 42×24'lük anahtar
 * değil. Metin ayrı bir `<label>` olsaydı dokunmatikte yazıya basmak hiçbir
 * şey yapmazdı.
 */
export function DashSwitchButton({
  on,
  label,
  hint,
  disabled,
  onToggle,
}: {
  on: boolean;
  label: ReactNode;
  /** Fare üstüne gelince görünen açıklama — zorunluluk değil, ipucu. */
  hint?: string;
  disabled?: boolean;
  onToggle: () => void;
}) {
  const travel = KNOB_TRAVEL * useInlineSign();

  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      className="dash-switch-btn"
      disabled={disabled}
      title={hint}
      onClick={onToggle}
    >
      <span className="dash-switch" data-on={on} aria-hidden="true">
        <motion.span
          className="dash-switch-knob"
          animate={{ x: on ? travel : 0 }}
          transition={spring.snap}
        />
      </span>
      <span className="dash-switch-btn-text">{label}</span>
    </button>
  );
}
